import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Printer, Search } from "lucide-react";
import { DsButton, DsIcon, DsInput } from "../../../../../design-system";

const WeeklyOffStructureTab = ({
  darkMode,
  weeklyOffPolicies,
  fetchAllMasterData,
  setShowWeeklyOffModal,
  handleEditWeeklyOffPolicy,
  handleDeleteWeeklyOffPolicy,
  formatDate,
  canCreate = true,
  canEdit = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [actionMenu, setActionMenu] = useState(null);

  const filteredPolicies = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return weeklyOffPolicies;
    return weeklyOffPolicies.filter((policy) =>
      String(policy?.name || "").toLowerCase().includes(query)
    );
  }, [searchTerm, weeklyOffPolicies]);

  const openActionMenuFromEvent = (e, policy) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 112;
    const menuApproxHeight = 84;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow < menuApproxHeight && rect.top > menuApproxHeight
        ? Math.max(8, rect.top - menuApproxHeight - 4)
        : rect.bottom + 4;
    setActionMenu((prev) =>
      String(prev?.policy?.id) === String(policy.id) ? null : { policy, top, left }
    );
  };

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [actionMenu]);

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
        <div className="ds-page-toolbar !rounded-none !border-x-0 !border-t-0 flex flex-wrap items-center justify-between gap-2 !shadow-none">
          <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Weekly Off Policies</h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <DsIcon
                icon={Search}
                size="sm"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
              />
              <DsInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search policy..."
                darkMode={darkMode}
                className="w-56 pl-8"
              />
            </div>
            <DsButton type="button" variant="outline" size="sm">
              <DsIcon icon={Printer} size="sm" />
              Print All
            </DsButton>
            {canCreate !== false ? (
            <DsButton
              type="button"
              variant="primary"
              size="sm"
              onClick={async () => {
                await fetchAllMasterData();
                setShowWeeklyOffModal(true);
              }}
            >
              Create New Policy
            </DsButton>
            ) : null}
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredPolicies.length === 0 ? (
            <p className="ds-caption p-6 text-center text-[hsl(var(--ds-muted-fg))]">
              No weekly off policies created yet
            </p>
          ) : (
            <table className="ds-table min-w-full text-sm">
              <thead className="ds-table-thead bg-[hsl(var(--ds-muted))]">
                <tr>
                  <th className="px-3 py-2 text-left">Policy</th>
                  <th className="px-3 py-2 text-left">Effective Date</th>
                  <th className="px-3 py-2 text-left">Applies To</th>
                  <th className="px-3 py-2 text-left">HR Policy Document</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPolicies.map((policy) => (
                  <tr
                    key={policy.id}
                    className="border-t border-[hsl(var(--ds-border))]"
                  >
                    <td className="px-3 py-2 font-medium">{policy.name}</td>
                    <td className="px-3 py-2">{formatDate(policy.effectiveDate)}</td>
                    <td className="px-3 py-2">
                      {policy.assignedTo?.allEmployees ? "All Employees" : "Custom Scope"}
                    </td>
                    <td className="px-3 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                      Policy Preview
                    </td>
                    <td className="px-3 py-2 text-right">
                      <DsButton
                        type="button"
                        variant="outline"
                        size="sm"
                        className="!h-8 !w-8 !px-0"
                        onClick={(e) => openActionMenuFromEvent(e, policy)}
                      >
                        <DsIcon icon={MoreVertical} size="sm" />
                      </DsButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {actionMenu &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[12360]" aria-hidden onClick={() => setActionMenu(null)} />
            <div
              className="fixed z-[12361] w-28 overflow-hidden rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 text-left shadow-[var(--ds-shadow-card)]"
              style={{ top: actionMenu.top, left: actionMenu.left }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  handleEditWeeklyOffPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
                className="block w-full px-3 py-2 text-left text-xs hover:bg-[hsl(var(--ds-muted))]"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  handleDeleteWeeklyOffPolicy(actionMenu.policy.id);
                  setActionMenu(null);
                }}
                className="block w-full px-3 py-2 text-left text-xs text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
              >
                Delete
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default WeeklyOffStructureTab;
